import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    withStyles,
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { AgGridColumn, AgGridReact } from 'ag-grid-react';
import { useEffect, useState } from 'react';
import { axiosInstance } from '../../utils/axiosInstance';
import { endpoint, snackbarDefaultValue, snackbarVariant, tableTranslations } from '../../utils/constants';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Snackbar from '../Snackbar/Snackbar';
import TitleHeader from '../TitleHeader/TitleHeader';

const formatBytes = size => {
    if (size == null) return '';
    if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    if (size >= 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${size} B`;
};

const styles = {
    root: {
        height: '100%',
        boxSizing: 'border-box',
        padding: '1rem 2rem',
        display: 'flex',
        flexDirection: 'column',
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-start',
        gap: '1rem',
        marginBottom: '1rem',
    },
    table: {
        fontFamily: 'inherit',
        width: '100%',
        flex: 1,
        minHeight: 0,
    },
    loadingWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        flex: 1,
    },
};

const LogActionsRenderer = ({ data, context }) => (
    <>
        <IconButton size="small" title="Podgląd" onClick={() => context.onView(data)}>
            <VisibilityIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" title="Pobierz" onClick={() => context.onDownload(data)}>
            <GetAppIcon fontSize="small" />
        </IconButton>
    </>
);

const FwLogs = ({ classes }) => {
    const [logs, setLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [preview, setPreview] = useState(null);
    const [snackbarData, setSnackbarData] = useState(snackbarDefaultValue);

    const showMessage = (message, variant) => {
        setSnackbarData({ isSnackbarOpen: true, message, variant, errorInfo: '' });
    };

    const handleIsSnackbarOpen = () => setSnackbarData(snackbarDefaultValue);

    const fetchLogs = () => {
        setIsLoading(true);
        axiosInstance
            .get(endpoint.FW_LOGS)
            .then(res => setLogs(res.data?.data ?? res.data ?? []))
            .catch(err =>
                showMessage(err.response?.data?.message || 'Błąd pobierania logów', snackbarVariant.ERROR)
            )
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        fetchLogs();
    }, []);

    const openPreview = log => {
        axiosInstance
            .get(`${endpoint.FW_LOGS}/${log.filename}`)
            .then(res => setPreview({ filename: res.data.filename, content: res.data.content }))
            .catch(err =>
                showMessage(err.response?.data?.message || 'Błąd odczytu logu', snackbarVariant.ERROR)
            );
    };

    const downloadLog = log => {
        axiosInstance
            .get(`${endpoint.FW_LOGS}/${log.filename}/download`, { responseType: 'blob' })
            .then(res => {
                const url = URL.createObjectURL(res.data);
                const link = document.createElement('a');
                link.href = url;
                link.download = log.filename;
                document.body.appendChild(link); // required for Firefox
                link.click();
                link.remove();
                URL.revokeObjectURL(url);
            })
            .catch(err =>
                showMessage(err.response?.data?.message || 'Błąd pobierania logu', snackbarVariant.ERROR)
            );
    };

    return (
        <div className={classes.root}>
            <TitleHeader title="Moduł FW — Logi" />
            <div className={classes.actions}>
                <Button onClick={fetchLogs} variant="outlined">
                    Odśwież
                </Button>
            </div>
            {isLoading ? (
                <div className={classes.loadingWrapper}>
                    <LoadingSpinner />
                </div>
            ) : (
                <div className={'ag-theme-alpine'} style={styles.table}>
                    <AgGridReact
                        localeText={tableTranslations}
                        rowData={logs}
                        suppressCellSelection
                        pagination
                        paginationPageSize={50}
                        enableCellTextSelection
                        accentedSort
                        suppressNoRowsOverlay={!isLoading}
                        context={{ onView: openPreview, onDownload: downloadLog }}>
                        {/* flex (not sizeColumnsToFit) keeps columns filling the grid width —
                            sizeColumnsToFit is a one-time mutation that gets undone when the
                            grid re-applies the column defs after a React re-render (e.g. the
                            preview modal opening), collapsing the columns to their width props. */}
                        <AgGridColumn
                            field="filename"
                            headerName="Nazwa logu"
                            flex={3}
                            sortable
                            filter
                            resizable
                        />
                        <AgGridColumn
                            field="size"
                            headerName="Rozmiar"
                            width={110}
                            flex={1}
                            valueFormatter={params => formatBytes(params.value)}
                            sortable
                            filter
                            resizable
                        />
                        <AgGridColumn
                            field="modified_at"
                            headerName="Data modyfikacji"
                            width={180}
                            flex={1}
                            sortable
                            filter
                            resizable
                        />
                        <AgGridColumn
                            headerName=""
                            width={90}
                            sortable={false}
                            filter={false}
                            cellRendererFramework={LogActionsRenderer}
                        />
                    </AgGridReact>
                </div>
            )}
            <Dialog open={Boolean(preview)} onClose={() => setPreview(null)} fullWidth maxWidth="md">
                <DialogTitle>{preview?.filename}</DialogTitle>
                <DialogContent dividers style={{ maxHeight: '60vh', overflow: 'auto' }}>
                    <pre
                        style={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                            fontFamily: 'monospace',
                            margin: 0,
                        }}>
                        {preview?.content}
                    </pre>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreview(null)} color="primary">
                        Zamknij
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar snackbarData={snackbarData} handleIsSnackbarOpen={handleIsSnackbarOpen} />
        </div>
    );
};

export default withStyles(styles)(FwLogs);
